import React from 'react';
import { Modal, Button, Alert, Badge } from 'react-bootstrap';
import type { DeleteCronJobModalProps } from './types';
import { getCronJobStatusText } from './types';

// Helper function to determine if a string is ISO datetime format
const isISOString = (str: string): boolean => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?Z?$/;
  return isoRegex.test(str);
};

// Helper function to format ISO string to readable date/time
const formatISOToDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const DeleteCronJobModal: React.FC<DeleteCronJobModalProps> = ({
  show,
  cronJob,
  onHide,
  onConfirm,
  isDeleting = false,
}) => {
  if (!cronJob) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Delete CronJob</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="danger">
          <Alert.Heading>Are you sure?</Alert.Heading>
          <p>
            You are about to delete the cron job <strong>"{cronJob.name}"</strong>.
          </p>
          <hr />
          <div className="mb-2">
            <strong>CronJob Details:</strong>
          </div>
          <ul className="mb-0">
            <li>
              <strong>ID:</strong> {cronJob.id}
            </li>
            <li>
              <strong>Status:</strong> {getCronJobStatusText(cronJob.status)}
            </li>
            <li>
              <strong>Schedule:</strong>{' '}
              {isISOString(cronJob.cronExpression) ? (
                <>
                  <Badge bg="info" className="me-2">
                    One Time
                  </Badge>
                  {formatISOToDateTime(cronJob.cronExpression)}
                </>
              ) : (
                <>
                  <Badge bg="secondary" className="me-2">
                    Recurring
                  </Badge>
                  <code>{cronJob.cronExpression}</code>
                </>
              )}
            </li>
            <li>
              <strong>HTTP Method:</strong> {cronJob.command.argument.method}
            </li>
            {(cronJob.command.argument as any)?.url && (
              <li>
                <strong>URL:</strong> {(cronJob.command.argument as any).url}
              </li>
            )}
          </ul>
        </Alert>

        <div className="bg-light p-3 rounded">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
            <div>
              <strong>Warning:</strong> This action cannot be undone. The scheduled job will be permanently removed and
              will no longer execute.
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isDeleting}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Delete CronJob'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteCronJobModal;
